import { forwardRef } from 'react'
import { tv } from 'tailwind-variants'
import { Spinner } from './Spinner'
import { cn } from '@/lib/utils'
import { Link, LinkProps } from 'react-router-dom'

type BaseButtonProps = {
	className?: string
	variant?: 'success' | 'danger' | 'outline' | 'frost'
	disabled?: boolean
}

export type ButtonProps = BaseButtonProps & {
	loading?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export type ButtonLinkProps = BaseButtonProps & LinkProps

const styles = tv({
	base: 'p-3 rounded-full text-[1.75rem] text-neutral relative text-center leading-none',
	variants: {
		color: {
			success: 'bg-success',
			danger: 'bg-danger',
			outline: 'bg-neutral border border-danger text-danger',
			frost: 'bg-neutral text-black'
		},
		disabled: {
			true: 'pointer-events-none'
		}
	}
})

const stateLayer = tv({
	base: 'absolute inset-0 flex items-center justify-center pointer-events-none rounded-full',
	variants: {
		isDisabled: {
			true: 'bg-neutral/30'
		}
	}
})

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
	const { className, loading, variant, children, ...rest } = props

	return (
		<button
			className={styles({
				color: variant,
				disabled: props.disabled,
				className: cn(className, {
					'pointer-events-none [&>*:not([data-spinner])]:invisible': loading
				})
			})}
			ref={ref}
			data-loading={loading}
			{...rest}
		>
			<div className={stateLayer({ isDisabled: props.disabled })} />
			{loading && (
				<span className="inset-0 absolute flex items-center justify-center size-full" data-spinner>
					<Spinner />
				</span>
			)}
			{typeof children === 'string' ? <span>{children}</span> : children}
		</button>
	)
})

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>((props, ref) => {
	const { className, variant, children, ...rest } = props

	return (
		<Link
			className={styles({
				color: variant,
				disabled: props.disabled,
				className
			})}
			ref={ref}
			{...rest}
		>
			<div className={stateLayer({ isDisabled: props.disabled })} />
			{children}
		</Link>
	)
})
